import { TournamentCompetition, type TournamentMatch } from "@mixitone/models";
import MixitoneController from "@mixitone/mvc";
import { List } from "@mixitone/oom";
import { withStateToggle } from "@mixitone/util";
import { TournamentDataAdapter } from "tournament-formats/TournamentDataAdapter";
import { buildGamesOverview, type GamesOverview } from "./gamesOverview";

interface State {
  loading: boolean;
  competitions: List<TournamentCompetition>;
  matches: TournamentMatch[];
  overview: GamesOverview;
}

interface Props {
  id: string;
}

function openConflictCountForCompetitions(competitions: TournamentCompetition[]): number {
  return competitions.reduce((sum, competition) => {
    const conflicts = competition.format_data?.tournament_conflicts;
    if (!Array.isArray(conflicts)) return sum;
    return (
      sum +
      conflicts.filter((conflict) => conflict && typeof conflict === "object" && conflict.status === "open")
        .length
    );
  }, 0);
}

/**
 * Drawer-scoped overview for the Games screen. Loads projected matches from the
 * tournament session id because routed modals do not share the page controller.
 */
class GamesOverviewController extends MixitoneController<State, Props> {
  get initialState(): State {
    return {
      loading: true,
      competitions: new List(),
      matches: [],
      overview: buildGamesOverview([]),
    };
  }

  override async initialize(props: Props) {
    await this.changeProps(props);
  }

  override async changeProps(props: Props) {
    if (!props.id) return;
    await this.loadOverview(props.id);
  }

  @withStateToggle("loading")
  private async loadOverview(tournamentSessionId: string) {
    const competitions = await TournamentCompetition.query()
      .eq("tournament_session_id", tournamentSessionId)
      .all();

    const adapter = new TournamentDataAdapter();
    const matches: TournamentMatch[] = [];
    for (const competition of competitions) {
      const projected = await adapter.loadProjectedMatches(competition);
      matches.push(...projected);
    }

    this.state.competitions = new List(...competitions);
    this.state.matches = matches;
    this.state.overview = buildGamesOverview(matches, {
      competitionIds: competitions.map((competition) => competition.id!).filter(Boolean),
      openConflictCount: openConflictCountForCompetitions(competitions),
    });
  }

  competitionName(competitionId: string): string {
    return this.state.competitions.findById(competitionId)?.name || "Competition";
  }
}

export default GamesOverviewController;
